import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import pharmacyService from '../../../services/pharmacyService';
import CompanyService from '../../../services/companyService';
import { usePharmacy } from '../pharmacyContext';
import { PHARMACY_COPY } from '../pharmacyCopy';
import { PharmacyPageHeader, StatCard, LoadingBlock, EmptyBlock, formatNumber } from '../ui';
import { todayYmd, daysAgoYmd } from '../../../utils/appTimezone';
import {
  PharmacyAlertsBar,
  PharmacyAlertsDoughnut,
  PharmacyChartCard
} from '../components/PharmacyAlertsCharts';
import { doughnutChartData, horizontalBarChartData, truncateLabel } from '../../head-doctor/reports/charts/chartDataHelpers';
import { formatReportMoney } from '../utils/reportFormat';
import PharmacyReportPrescriptionsTable from '../components/PharmacyReportPrescriptionsTable';
import PharmacyReportsMedicationsSection from '../components/PharmacyReportsMedicationsSection';
import ReportsFilterBar from '../../head-doctor/reports/ReportsFilterBar';
import { REPORTS_PERIOD_PRESETS } from '../../head-doctor/reports/reportsNavConfig';
import '../../head-doctor/HeadDoctorDashboard.css';

const copy = PHARMACY_COPY.REPORTS;

const TABS = [
  { id: 'synthesis', label: copy.TAB_SYNTHESIS },
  { id: 'prescriptions', label: copy.TAB_PRESCRIPTIONS },
  { id: 'medications', label: copy.TAB_MEDICATIONS }
];

const MEDICATIONS_RANK_LIMIT = 10;

function detectActivePreset(from, to) {
  const today = todayYmd();
  if (to !== today) return null;
  const match = REPORTS_PERIOD_PRESETS.find((preset) => from === daysAgoYmd(preset.days));
  return match?.key || null;
}

function synthesisTopMedChart(rows) {
  if (!rows?.length) return null;
  return horizontalBarChartData(
    rows.slice(0, 5).map((r) => truncateLabel(r.medication_name, 22)),
    rows.slice(0, 5).map((r) => Number(r.prescription_count || 0)),
    copy.CHART_AXIS_COUNT,
    ['#0f766e', '#0d9488', '#14b8a6', '#2dd4bf', '#5eead4']
  );
}

export default function PharmacyReportsPage({ embedded = false }) {
  const { workSiteId, workSites } = usePharmacy();
  const [tab, setTab] = useState('synthesis');
  const [from, setFrom] = useState(daysAgoYmd(30));
  const [to, setTo] = useState(todayYmd());
  const [siteFilter, setSiteFilter] = useState(() => (workSiteId ? String(workSiteId) : ''));
  const [companyFilter, setCompanyFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [companies, setCompanies] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (workSiteId) setSiteFilter(String(workSiteId));
  }, [workSiteId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await CompanyService.getActiveCompanies();
        if (!cancelled && res.success) {
          setCompanies(Array.isArray(res.data) ? res.data : []);
        }
      } catch {
        if (!cancelled) setCompanies([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const activePreset = useMemo(() => detectActivePreset(from, to), [from, to]);

  const handlePreset = useCallback((preset) => {
    setTo(todayYmd());
    setFrom(daysAgoYmd(preset.days));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await pharmacyService.reportOverview({
        from,
        to,
        site_id: siteFilter || undefined,
        company_id: companyFilter || undefined,
        status: statusFilter || undefined,
        top_limit: MEDICATIONS_RANK_LIMIT
      });
      setData(res.success ? res.data : null);
    } finally {
      setLoading(false);
    }
  }, [from, to, siteFilter, companyFilter, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const synthesis = data?.synthesis;
  const prescriptionLines = data?.prescriptions?.lines || [];
  const topMeds = data?.medications?.top_prescribed || [];
  const leastMeds = data?.medications?.least_prescribed || [];

  const originPie = useMemo(() => {
    if (!synthesis) return null;
    const rx = Number(synthesis.rx_dispensations || 0);
    const manual = Number(synthesis.manual_dispensations || 0);
    if (rx + manual <= 0) return null;
    return doughnutChartData(
      [copy.STAT_RX, copy.STAT_MANUAL],
      [rx, manual],
      ['#4338ca', '#0d9488']
    );
  }, [synthesis]);

  const topMedChart = useMemo(() => synthesisTopMedChart(topMeds), [topMeds]);

  return (
    <div className={`cp-reports-page${embedded ? ' cp-reports-page--embedded' : ''}`}>
      {embedded ? null : (
        <PharmacyPageHeader badge={copy.BADGE} title={copy.TITLE} description={copy.DESC} />
      )}

      <div className="cp-reports-filters">
        <div className="cp-reports-filters__head">
          <span className="cp-reports-filters__eyebrow">{copy.FILTERS_EYEBROW}</span>
          <p className="cp-reports-filters__hint">{copy.FILTERS_HINT}</p>
        </div>

        <ReportsFilterBar
          copy={copy}
          allowAllSites
          showSiteFilter={workSites.length > 0}
          showCompanyFilter
          from={from}
          to={to}
          siteFilter={siteFilter}
          companyFilter={companyFilter}
          workSites={workSites}
          companies={companies}
          loading={loading}
          activePreset={activePreset}
          onFromChange={setFrom}
          onToChange={setTo}
          onSiteChange={setSiteFilter}
          onCompanyChange={setCompanyFilter}
          onPreset={handlePreset}
          onRefresh={load}
        >
          <label className="hd-reports-filterbar__field hd-reports-filterbar__field--status">
            <span className="hd-reports-filterbar__label">{copy.FILTER_STATUS_SHORT}</span>
            <Form.Select
              size="sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              disabled={loading}
              aria-label={copy.FILTER_STATUS_ARIA}
            >
              <option value="">{copy.FILTER_STATUS_ALL}</option>
              <option value="DISPENSED">{copy.FILTER_STATUS_DISPENSED}</option>
              <option value="PENDING">{copy.FILTER_STATUS_PENDING}</option>
            </Form.Select>
          </label>
        </ReportsFilterBar>
      </div>

      <div className="cp-filter-pills cp-reports-tabs" role="tablist" aria-label="Sections rapport">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={`cp-filter-pill${tab === t.id ? ' is-active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <LoadingBlock label={copy.LOADING} />
      ) : !data ? (
        <EmptyBlock label={copy.UNAVAILABLE} />
      ) : (
        <>
          {tab === 'synthesis' ? (
            <>
              <section className="cp-alerts-stats cp-reports-stats" aria-label="Synthèse">
                <StatCard label={copy.STAT_DISPENSATIONS} value={formatNumber(synthesis.dispensations)} icon="pills" />
                <StatCard label={copy.STAT_LINES} value={formatNumber(synthesis.lines)} icon="list" />
                <StatCard
                  label={copy.STAT_TOTAL_AMOUNT}
                  value={formatReportMoney(synthesis.total_amount)}
                  tone="green"
                  icon="chartBar"
                />
                <StatCard
                  label={copy.STAT_AVG_VALUE}
                  value={formatReportMoney(synthesis.avg_prescription_value)}
                  tone="amber"
                  icon="chartBar"
                />
                <StatCard label={copy.STAT_RX} value={formatNumber(synthesis.rx_dispensations)} icon="stethoscope" />
                <StatCard label={copy.STAT_MANUAL} value={formatNumber(synthesis.manual_dispensations)} icon="edit" />
                <StatCard label={copy.STAT_MEDS} value={formatNumber(synthesis.medications_prescribed)} icon="warehouse" />
              </section>

              {synthesis.unpriced_lines > 0 ? (
                <p className="cp-reports-hint">{copy.NO_PRICE_HINT}</p>
              ) : null}

              <section className="cp-alerts-charts">
                <div className="cp-alerts-charts__row">
                  <PharmacyChartCard title={copy.CHART_ORIGIN_TITLE} subtitle={copy.CHART_ORIGIN_DESC}>
                    <PharmacyAlertsDoughnut data={originPie} />
                  </PharmacyChartCard>
                  <PharmacyChartCard
                    title={copy.CHART_TOP_MED_TITLE}
                    subtitle={copy.CHART_TOP_MED_DESC}
                    height={Math.max(240, Math.min(topMeds.length, 5) * 38 + 72)}
                  >
                    <PharmacyAlertsBar
                      data={topMedChart}
                      horizontal
                      xLabel={copy.CHART_AXIS_COUNT}
                      options={{
                        layout: { padding: { right: 28 } },
                        scales: {
                          x: { grace: '10%', ticks: { precision: 0 } },
                          y: { ticks: { font: { size: 11, weight: '600' } } }
                        }
                      }}
                    />
                  </PharmacyChartCard>
                </div>
              </section>
            </>
          ) : null}

          {tab === 'prescriptions' ? (
            <>
              <section className="cp-alerts-stats cp-reports-stats" aria-label="Synthèse ordonnances">
                <StatCard label={copy.STAT_DISPENSATIONS} value={formatNumber(synthesis.dispensations)} icon="pills" />
                <StatCard label={copy.STAT_LINES} value={formatNumber(synthesis.lines)} icon="list" />
                <StatCard
                  label={copy.STAT_TOTAL_AMOUNT}
                  value={formatReportMoney(synthesis.total_amount)}
                  tone="green"
                  icon="chartBar"
                />
                <StatCard
                  label={copy.STAT_AVG_VALUE}
                  value={formatReportMoney(synthesis.avg_prescription_value)}
                  tone="amber"
                  icon="chartBar"
                />
                <StatCard label={copy.STAT_RX} value={formatNumber(synthesis.rx_dispensations)} icon="stethoscope" />
                <StatCard label={copy.STAT_MANUAL} value={formatNumber(synthesis.manual_dispensations)} icon="edit" />
                <StatCard label={copy.STAT_MEDS} value={formatNumber(synthesis.medications_prescribed)} icon="warehouse" />
              </section>

              {synthesis.unpriced_lines > 0 ? (
                <p className="cp-reports-hint">{copy.NO_PRICE_HINT}</p>
              ) : null}

              <div className="cp-card cp-reports-list-card">
                <div className="cp-reports-section-head">
                  <div>
                    <h2 className="cp-reports-section-head__title">{copy.TAB_PRESCRIPTIONS}</h2>
                    <p className="cp-reports-section-head__meta mb-0">
                      {copy.PRESCRIPTIONS_AVG} :{' '}
                      <strong>{formatReportMoney(data.prescriptions.avg_value)}</strong>
                      {' · '}
                      {copy.COL_DATE} DESC
                    </p>
                  </div>
                </div>
                <div className="cp-card__body cp-card__body--flush">
                  <PharmacyReportPrescriptionsTable
                    lines={prescriptionLines}
                    groups={data.prescriptions?.groups || []}
                    avgValue={data.prescriptions?.avg_value || 0}
                  />
                </div>
              </div>
            </>
          ) : null}

          {tab === 'medications' ? (
            <PharmacyReportsMedicationsSection
              topMeds={topMeds}
              leastMeds={leastMeds}
              topInStock={data.medications?.top_in_stock || []}
              groups={data.prescriptions?.groups || []}
              siteId={siteFilter}
            />
          ) : null}
        </>
      )}
    </div>
  );
}
